"use client";

import { c as _c } from "react/compiler-runtime";
import { useOrganizationsControllerFindAll, useOrganizationsControllerFindOne } from "@dc/gateway-jsonapi";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useAbility } from "@/hooks/useAbility";
import type { EntitlementCheckResult, OrganizationContextType, OrganizationProviderProps, TrialSummary } from "./OrganizationProvider.types";
export const OrganizationContext = createContext<OrganizationContextType | null>(null);
export function OrganizationProvider(t0) {
  const $ = _c(30);
  const {
    children,
    orgId: orgIdProp
  } = t0;
  const {
    data: response,
    error,
    isPending
  } = useOrganizationsControllerFindAll();
  const [selectedOrgId, setSelectedOrgId] = useState(orgIdProp ?? null);
  const [isUpdating, setIsUpdating] = useState(false);
  const {
    can,
    isLoading: isAbilityLoading
  } = useAbility();
  const isSystemAdmin = can("manage", {
    scopeType: "SYSTEM"
  });
  const isRouteControlled = !!orgIdProp;
  let t1;
  let t2;
  if ($[0] !== orgIdProp) {
    t1 = () => {
      if (orgIdProp) {
        setSelectedOrgId(orgIdProp);
      }
    };
    t2 = [orgIdProp];
    $[0] = orgIdProp;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  response?.data;
  let t3;
  if ($[3] !== response?.data) {
    t3 = response?.data || [];
    $[3] = response?.data;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const organizations = t3;
  const t4 = isSystemAdmin ? "trial" : "trial,entitlement";
  let t5;
  if ($[5] !== t4) {
    t5 = {
      include: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !!selectedOrgId && !isAbilityLoading;
  let t7;
  if ($[7] !== t6) {
    t7 = {
      query: {
        enabled: t6
      }
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const {
    data: currentOrgResponse,
    error: currentOrgError,
    isPending: isPendingCurrentOrg
  } = useOrganizationsControllerFindOne(selectedOrgId ?? "", t5, t7);
  const isAccessDenied = isRouteControlled && !!currentOrgError;
  const orgAccessErrorStatus = (currentOrgError as {
    response?: {
      status?: number;
    };
  } | null)?.response?.status ?? null;
  const currentOrganization = currentOrgResponse?.data ?? null;
  const currentEntitlement = (currentOrganization?.attributes as {
    entitlement?: EntitlementCheckResult;
  } | undefined)?.entitlement ?? null;
  const included = currentOrgResponse?.included as {
    id: string;
    type: string;
    attributes: Record<string, unknown>;
  }[] | undefined;
  let t8;
  if ($[9] !== included) {
    t8 = included?.find(_temp);
    $[9] = included;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const trialIncluded = t8;
  let t9;
  if ($[11] !== trialIncluded) {
    t9 = trialIncluded ? {
      id: trialIncluded.id,
      status: trialIncluded.attributes.status as TrialSummary["status"],
      projectCount: trialIncluded.attributes.projectCount as TrialSummary["projectCount"],
      projectCap: (trialIncluded.attributes.projectCap ?? null) as TrialSummary["projectCap"],
      expiresAt: (trialIncluded.attributes.expiresAt ?? null) as TrialSummary["expiresAt"]
    } : null;
    $[11] = trialIncluded;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const currentTrial = t9;
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = organizationOrId => {
      const orgId = typeof organizationOrId === "object" ? organizationOrId.id : organizationOrId;
      setIsUpdating(true);
      setSelectedOrgId(orgId);
      setTimeout(() => setIsUpdating(false), 100);
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const setCurrentOrganization = t10;
  const t11 = isPending || !!selectedOrgId && isPendingCurrentOrg;
  let t12;
  if ($[14] !== error) {
    t12 = error ? error instanceof Error ? error : new Error(String(error)) : null;
    $[14] = error;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== currentEntitlement || $[17] !== currentOrganization || $[18] !== currentTrial || $[19] !== isAccessDenied || $[20] !== isUpdating || $[21] !== orgAccessErrorStatus || $[22] !== organizations || $[23] !== selectedOrgId || $[24] !== t11 || $[25] !== t12) {
    t13 = {
      currentOrganization,
      currentOrganizationId: selectedOrgId,
      currentTrial,
      currentEntitlement,
      organizations,
      isLoading: t11,
      error: t12,
      setCurrentOrganization,
      isUpdating,
      isAccessDenied,
      orgAccessErrorStatus
    };
    $[16] = currentEntitlement;
    $[17] = currentOrganization;
    $[18] = currentTrial;
    $[19] = isAccessDenied;
    $[20] = isUpdating;
    $[21] = orgAccessErrorStatus;
    $[22] = organizations;
    $[23] = selectedOrgId;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const value = t13;
  let t14;
  if ($[27] !== children || $[28] !== value) {
    t14 = <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
    $[27] = children;
    $[28] = value;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}
function _temp(i) {
  return i.type === "trial-summaries";
}